import { useCallback, useEffect, useMemo, useState } from 'react'
import { RoomGroup } from './RoomGroup'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { X, Search, FileDown } from 'lucide-react'
import {
  createRoom,
  deleteRoom,
  duplicateRoom,
  fetchDashboardKPIs,
  getRoomDetails,
  updateRoomDetails,
  leaveProfessorGame,
} from '@/redux/asyncThunks/room'
import { AppDispatch, RootState } from '@/redux/store'
import { fetchRooms } from '@/redux/asyncThunks/room'
import RoomCard from './RoomCard'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import LeaveGameModal from './LeaveGameModal'
import RoomModal from './CreateRoomModal'
import { ExportOptionsModal } from './ExportOptionsModal'
import { ImportModal } from './ImportModal'
import ROUTES from '@/routes/routes'
import {
  CardType,
  GameRoomStatus,
  ModeType,
  RoleChoice,
  RoomModalMode,
  Status,
} from '@/redux/types/room'
import { LoadingOverlay } from '../settings/LoadingOverlay'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import { toast } from 'sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

const Room: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const [confirmDuplicateModalOpen, setConfirmDuplicateModalOpen] =
    useState(false)
  const [roomModalOpen, setRoomModalOpen] = useState(false)
  const [roomModalMode, setRoomModalMode] = useState<RoomModalMode>(
    RoomModalMode.CREATE
  )
  const [roomToDelete, setRoomToDelete] = useState<string | null>(null)
  const [roomToDuplicate, setRoomToDuplicate] = useState<string | null>(null)
  const [roomToEdit, setRoomToEdit] = useState<{
    id: string
    name: string
  } | null>(null)
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [roomToExport, setRoomToExport] = useState<{
    id: string
    isLive: boolean
    gameMode: TimerMode
  } | null>(null)
  const [importGameModalOpen, setImportGameModalOpen] = useState(false)
  const [sortBy, setSortBy] = useState<string>('roomname')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('')
  const [roomToLeave, setRoomToLeave] = useState<{
    roomId: string
    isOwner: boolean
  } | null>(null)

  const { rooms, isLoading, dashboardKPIs } = useSelector(
    (state: RootState) => state.room
  )

  const user = useSelector((state: RootState) => state.user.user)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  const refreshDashboardData = useCallback(() => {
    dispatch(fetchRooms({ sortBy, searchQuery: debouncedSearchQuery }))
    dispatch(fetchDashboardKPIs())
  }, [dispatch, sortBy, debouncedSearchQuery])

  useEffect(() => {
    refreshDashboardData()
  }, [refreshDashboardData])

  const handleCreateRoomClick = () => {
    setRoomModalMode(RoomModalMode.CREATE)
    setRoomToEdit(null)
    setRoomModalOpen(true)
  }

  const handleCreateRoom = async (roomName: string, timerMode?: TimerMode) => {
    try {
      await dispatch(
        createRoom({
          user: user!,
          roomName,
          timerMode: timerMode || TimerMode.SINGLE_ROUND,
        })
      ).unwrap()
      refreshDashboardData()
      setRoomModalOpen(false)
    } catch (error) {
      console.error('Failed to create room:', error)
    }
  }

  const handleDeleteClick = (roomId: string) => {
    setRoomToDelete(roomId)
    setConfirmDeleteModalOpen(true)
  }

  const handleEditRoomClick = (roomId: string) => {
    const room = rooms.find((r) => r.roomid === roomId)
    if (room) {
      setRoomToEdit({ id: roomId, name: room.roomname || '' })
      setRoomModalMode(RoomModalMode.EDIT)
      setRoomModalOpen(true)
    }
  }

  const handleEditRoom = async (roomName: string) => {
    if (!roomToEdit) return
    dispatch(getRoomDetails(roomToEdit.id))
    try {
      await dispatch(
        updateRoomDetails({
          roomId: roomToEdit.id,
          roomname: roomName,
        })
      ).unwrap()
      refreshDashboardData()
      setRoomModalOpen(false)
      setRoomToEdit(null)
    } catch (error) {
      console.error('Failed to update room:', error)
    }
  }

  const handleLockRoom = async (roomId: string, is_locked: boolean) => {
    if (!roomId) return
    dispatch(getRoomDetails(roomId))
    try {
      await dispatch(
        updateRoomDetails({
          roomId: roomId,
          is_locked: is_locked,
        })
      ).unwrap()
      refreshDashboardData()
    } catch (error) {
      console.error('Failed to update room lock status:', error)
    }
  }

  const handleRoomModalConfirm = (roomName: string, timerMode?: TimerMode) => {
    if (roomModalMode === RoomModalMode.CREATE) {
      handleCreateRoom(roomName, timerMode)
    } else {
      handleEditRoom(roomName)
    }
  }

  const handleDeleteRoom = async () => {
    if (!roomToDelete) return

    try {
      await dispatch(deleteRoom(roomToDelete)).unwrap()
      refreshDashboardData()
    } catch (error: any) {
      console.error('Failed to delete room:', error)
    } finally {
      setRoomToDelete(null)
      setConfirmDeleteModalOpen(false)
    }
  }

  const handleLeaveClick = (roomId: string, isOwner: boolean) => {
    setRoomToLeave({ roomId, isOwner })
  }

  const handleConfirmLeave = async (newOwnerId?: number) => {
    if (!roomToLeave) return
    await dispatch(
      leaveProfessorGame({ roomId: roomToLeave.roomId, newOwnerId })
    ).unwrap()
    toast.success('You have left the game room.')
    setRoomToLeave(null)
    refreshDashboardData()
  }

  const handleDuplicateRoomClick = async (roomId: string) => {
    setRoomToDuplicate(roomId)
    setConfirmDuplicateModalOpen(true)
  }

  const duplicateWithAssignments = async () => {
    if (!roomToDuplicate) return

    try {
      setConfirmDuplicateModalOpen(false)
      setIsDuplicating(true)
      await dispatch(
        duplicateRoom({ roomId: roomToDuplicate, withAssignments: true })
      ).unwrap()
      toast.success(
        'Room duplicated successfully! Documents are being processed in the background.',
        {
          duration: 5000,
        }
      )
      refreshDashboardData()
      setRoomToDuplicate(null)
    } catch (error: any) {
      console.error('Failed to duplicate room:', error)
      const errorMessage = error?.message || 'An unexpected error occurred'

      // Check for timeout or network errors
      if (
        errorMessage.includes('timeout') ||
        errorMessage.includes('Network Error') ||
        errorMessage.includes('ERR_FAILED') ||
        errorMessage.includes('CORS')
      ) {
        toast.error(
          'Room duplication is taking longer than expected. The room may still be created. Please refresh the page to check.',
          { duration: 8000 }
        )
      } else {
        toast.error(`Failed to duplicate room: ${errorMessage}`)
      }
      setRoomToDuplicate(null)
    } finally {
      setIsDuplicating(false)
    }
  }

  const duplicateWithoutAssignments = async () => {
    if (!roomToDuplicate) return

    try {
      setConfirmDuplicateModalOpen(false)
      setIsDuplicating(true)
      await dispatch(
        duplicateRoom({ roomId: roomToDuplicate, withAssignments: false })
      ).unwrap()
      toast.success(
        'Room duplicated successfully! Documents are being processed in the background.',
        {
          duration: 5000,
        }
      )
      refreshDashboardData()
      setRoomToDuplicate(null)
    } catch (error: any) {
      console.error('Failed to duplicate room:', error)
      const errorMessage = error?.message || 'An unexpected error occurred'

      // Check for timeout or network errors
      if (
        errorMessage.includes('timeout') ||
        errorMessage.includes('Network Error') ||
        errorMessage.includes('ERR_FAILED') ||
        errorMessage.includes('CORS')
      ) {
        toast.error(
          'Room duplication is taking longer than expected. The room may still be created. Please refresh the page to check.',
          { duration: 8000 }
        )
      } else {
        toast.error(`Failed to duplicate room: ${errorMessage}`)
      }
      setRoomToDuplicate(null)
    } finally {
      setIsDuplicating(false)
    }
  }

  const handleExportRoomClick = (roomId: string) => {
    const room = rooms.find((r) => r.roomid === roomId)
    setRoomToExport({
      id: roomId,
      isLive: room?.status === GameRoomStatus.IN_PROGRESS,
      gameMode: room?.game_mode ?? TimerMode.SINGLE_ROUND,
    })
    setExportModalOpen(true)
  }

  const handleEnterRoom = (
    roomid: string,
    invitationStatus: Status,
    role?: RoleChoice | null
  ) => {
    // Owners/co-professors go straight to the room even while their invite is
    // still pending (the room view authorizes them). Only genuine pending
    // players need the join-room flow. (#566: co-professor stuck on join-room.)
    const isStaff =
      role === RoleChoice.OWNER || role === RoleChoice.CO_PROFESSOR
    if (invitationStatus === Status.Pending && !isStaff) {
      navigate(ROUTES.PROFESSOR.JOIN_ROOM(roomid))
    } else {
      navigate(ROUTES.PROFESSOR.ROOM_DETAILS(roomid))
    }
  }
  const handleRefresh = useCallback(() => {
    refreshDashboardData()
  }, [refreshDashboardData])

  const handleSortChange = (value: string) => {
    setSortBy(value)
  }

  const handleRoomModalClose = () => {
    setRoomModalOpen(false)
    setRoomToEdit(null)
  }

  const roomGroups = useMemo(() => {
    const live = rooms.filter(
      (r) =>
        r.status === GameRoomStatus.IN_PROGRESS ||
        r.status === GameRoomStatus.PAUSED
    )
    const readyToStart = rooms.filter(
      (r) => r.status === GameRoomStatus.NOT_STARTED && r.is_ready_to_start
    )
    const notReady = rooms.filter(
      (r) => r.status === GameRoomStatus.NOT_STARTED && !r.is_ready_to_start
    )
    const completed = rooms.filter(
      (r) => r.status === GameRoomStatus.COMPLETED
    )
    return { live, readyToStart, notReady, completed }
  }, [rooms])

  const dashboardStats = useMemo(
    () => [
      {
        label: 'Total Games',
        value: dashboardKPIs.total_games,
      },
      {
        label: 'Waiting',
        value: dashboardKPIs.scheduled_games,
      },
      {
        label: 'Active',
        value: dashboardKPIs.active_games,
      },
      {
        label: 'Completed',
        value: dashboardKPIs.completed_games,
      },
      {
        label: 'Players',
        value: dashboardKPIs.distinct_players,
      },
      {
        label: 'Professors',
        value: dashboardKPIs.distinct_professors,
      },
      {
        label: 'Pending Adjudications',
        value: dashboardKPIs.pending_adjudications,
      },
      // Charter Classes are counted separately from games — every counter
      // above is games only.
      {
        label: 'Charter Classes',
        value: dashboardKPIs.charter_classes,
      },
    ],
    [dashboardKPIs]
  )

  return (
    <div className='px-4 py-6 w-full min-h-full sm:px-6 md:px-8 lg:px-12'>
      <LoadingOverlay isVisible={isDuplicating} message='Duplicating room...' />
      <ConfirmationModal
        isOpen={confirmDeleteModalOpen}
        onClose={() => setConfirmDeleteModalOpen(false)}
        onConfirm={handleDeleteRoom}
        title='Delete Game Room'
        description='Are you sure you want to delete this game room? This action cannot be undone.'
        confirmButtonText='Delete'
        cancelButtonText='Cancel'
      />

      <ConfirmationModal
        isOpen={confirmDuplicateModalOpen}
        onClose={() => setConfirmDuplicateModalOpen(false)}
        onConfirm={duplicateWithAssignments}
        title='Duplicate Game Room'
        description='Do you want to include chatbot assignments in the duplicate?'
        confirmButtonText='Yes'
        cancelButtonText='Cancel'
        secondaryActionText='No'
        onSecondaryAction={duplicateWithoutAssignments}
      />

      <LeaveGameModal
        isOpen={Boolean(roomToLeave)}
        onClose={() => setRoomToLeave(null)}
        onConfirm={handleConfirmLeave}
        isOwner={roomToLeave?.isOwner ?? false}
        roomId={roomToLeave?.roomId ?? ''}
      />

      <RoomModal
        modeType={ModeType.ROOM}
        isOpen={roomModalOpen}
        onClose={handleRoomModalClose}
        onConfirm={handleRoomModalConfirm}
        isLoading={isLoading}
        mode={roomModalMode}
        currentRoomName={roomToEdit?.name || ''}
      />

      {rooms?.length === 0 && (
        <div className='flex fixed inset-0 justify-center items-center -z-10'>
          <img
            src='images/add-files.svg'
            alt='Add Files'
            className='h-[297px] w-[332px]'
          />
        </div>
      )}
      <div className='flex gap-4 justify-between items-start mb-6'>
        <div className='space-y-1'>
          <h1 className='text-2xl font-semibold text-dark-blue'>Dashboard</h1>
          <p className='text-sm text-slate-600'>
            Track all games, review their status, and jump into a room when you
            need to manage it.
          </p>
        </div>
        <div className='flex gap-3 items-center'>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2' />
            <Input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search by room name or ID'
              className='pr-10 pl-10 w-64'
            />
            {searchQuery && (
              <Button
                onClick={() => setSearchQuery('')}
                variant='ghost'
                size='sm'
                className='absolute right-1 top-1/2 p-1 h-auto -translate-y-1/2 hover:bg-transparent'
                title='Clear search'
              >
                <X className='w-4 h-4 text-gray-400 hover:text-gray-600' />
              </Button>
            )}
          </div>
          <div className='flex gap-2 items-center'>
            <label
              htmlFor='sort-select'
              className='text-sm whitespace-nowrap text-dark-blue'
            >
              Sort by:
            </label>
            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger id='sort-select' className='w-[180px]'>
                <SelectValue placeholder='Select sort option' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='roomname'>Room Name</SelectItem>
                <SelectItem value='roomid'>Room ID</SelectItem>
                <SelectItem value='players_count'>Number of Players</SelectItem>
                <SelectItem value='status'>Game Status</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div
            onClick={handleRefresh}
            className='p-1 bg-transparent rounded-md cursor-pointer'
          >
            <img src='/icons/refresh.svg' alt='Refresh' className='size-5' />
          </div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='gap-2 border-dark-blue text-dark-blue'
            onClick={() => setImportGameModalOpen(true)}
          >
            <FileDown className='w-4 h-4' />
            Import game
          </Button>
        </div>
      </div>
      <div className='grid grid-cols-2 gap-3 mb-6 md:grid-cols-4 xl:grid-cols-7'>
        {dashboardStats.map((stat) => (
          <div
            key={stat.label}
            className='px-4 py-3 bg-white rounded-xl border shadow-sm border-slate-200'
          >
            <p className='text-sm text-slate-500'>{stat.label}</p>
            <p className='mt-1 text-lg font-semibold text-dark-blue'>
              {stat.value}
            </p>
          </div>
        ))}
      </div>
      <div className='grid grid-cols-1 gap-5 mb-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'>
        <div
          onClick={handleCreateRoomClick}
          className="flex items-center justify-center min-h-[230px] bg-[#023572] bg-[url('/images/create-room-bg.png')] bg-cover bg-center rounded-xl cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] border border-white/10"
        >
          <div className='flex flex-col gap-3 items-center'>
            <img
              src='/icons/plus-rounded.svg'
              alt='Plus'
              className='w-16 h-16'
            />
            <span className='text-lg font-medium text-white'>Create Room</span>
          </div>
        </div>
      </div>

      <RoomGroup
        title='Live Games'
        count={roomGroups.live.length}
        dotColor='emerald'
        defaultOpen={true}
      >
        {roomGroups.live.map((room) => (
          <RoomCard
            cardType={CardType.ROOM}
            key={room.roomid}
            roomId={room.roomid}
            inviteCode={room.invite_code}
            roomName={room.roomname || undefined}
            onDeleteClick={() => handleDeleteClick(room.roomid)}
            onEditClick={() => handleEditRoomClick(room.roomid)}
            onLockedClick={handleLockRoom}
            onDuplicateClick={() => handleDuplicateRoomClick(room.roomid)}
            onExportClick={() => handleExportRoomClick(room.roomid)}
            buttonText='Enter Room'
            showBadge={true}
            status={room.status}
            onButtonClick={() =>
              handleEnterRoom(room.roomid, room.invitation_status, room.role)
            }
            isProfessor={true}
            pendingAdjudicationsCount={room.pending_adjudications_count || 0}
            advisorsCount={room.advisors_count || 0}
            playersCount={room.players_count || 0}
            isGameReadyToStart={room.is_ready_to_start}
            scenarioTitle={room.scenario_title!}
            isLocked={room.is_locked}
            isOwner={user?.email === room.owner}
            isSimulating={room.is_simulating}
            onLeaveClick={() =>
              handleLeaveClick(room.roomid, user?.email === room.owner)
            }
          />
        ))}
      </RoomGroup>

      <RoomGroup
        title='Ready to Start'
        count={roomGroups.readyToStart.length}
        dotColor='sky'
        defaultOpen={true}
      >
        {roomGroups.readyToStart.map((room) => (
          <RoomCard
            cardType={CardType.ROOM}
            key={room.roomid}
            roomId={room.roomid}
            inviteCode={room.invite_code}
            roomName={room.roomname || undefined}
            onDeleteClick={() => handleDeleteClick(room.roomid)}
            onEditClick={() => handleEditRoomClick(room.roomid)}
            onLockedClick={handleLockRoom}
            onDuplicateClick={() => handleDuplicateRoomClick(room.roomid)}
            onExportClick={() => handleExportRoomClick(room.roomid)}
            buttonText='Enter Room'
            showBadge={true}
            status={room.status}
            onButtonClick={() =>
              handleEnterRoom(room.roomid, room.invitation_status, room.role)
            }
            isProfessor={true}
            pendingAdjudicationsCount={room.pending_adjudications_count || 0}
            advisorsCount={room.advisors_count || 0}
            playersCount={room.players_count || 0}
            isGameReadyToStart={room.is_ready_to_start}
            scenarioTitle={room.scenario_title!}
            isLocked={room.is_locked}
            isOwner={user?.email === room.owner}
            isSimulating={room.is_simulating}
            onLeaveClick={() =>
              handleLeaveClick(room.roomid, user?.email === room.owner)
            }
          />
        ))}
      </RoomGroup>

      <RoomGroup
        title='Not Ready'
        count={roomGroups.notReady.length}
        dotColor='amber'
        defaultOpen={true}
      >
        {roomGroups.notReady.map((room) => (
          <RoomCard
            cardType={CardType.ROOM}
            key={room.roomid}
            roomId={room.roomid}
            inviteCode={room.invite_code}
            roomName={room.roomname || undefined}
            onDeleteClick={() => handleDeleteClick(room.roomid)}
            onEditClick={() => handleEditRoomClick(room.roomid)}
            onLockedClick={handleLockRoom}
            onDuplicateClick={() => handleDuplicateRoomClick(room.roomid)}
            onExportClick={() => handleExportRoomClick(room.roomid)}
            buttonText='Enter Room'
            showBadge={true}
            status={room.status}
            onButtonClick={() =>
              handleEnterRoom(room.roomid, room.invitation_status, room.role)
            }
            isProfessor={true}
            pendingAdjudicationsCount={room.pending_adjudications_count || 0}
            advisorsCount={room.advisors_count || 0}
            playersCount={room.players_count || 0}
            isGameReadyToStart={room.is_ready_to_start}
            scenarioTitle={room.scenario_title!}
            isLocked={room.is_locked}
            isOwner={user?.email === room.owner}
            isSimulating={room.is_simulating}
            onLeaveClick={() =>
              handleLeaveClick(room.roomid, user?.email === room.owner)
            }
          />
        ))}
      </RoomGroup>

      <RoomGroup
        title='Completed'
        count={roomGroups.completed.length}
        dotColor='slate'
        defaultOpen={false}
      >
        {roomGroups.completed.map((room) => (
          <RoomCard
            cardType={CardType.ROOM}
            key={room.roomid}
            roomId={room.roomid}
            inviteCode={room.invite_code}
            roomName={room.roomname || undefined}
            onDeleteClick={() => handleDeleteClick(room.roomid)}
            onEditClick={() => handleEditRoomClick(room.roomid)}
            onLockedClick={handleLockRoom}
            onDuplicateClick={() => handleDuplicateRoomClick(room.roomid)}
            onExportClick={() => handleExportRoomClick(room.roomid)}
            buttonText='Enter Room'
            showBadge={true}
            status={room.status}
            onButtonClick={() =>
              handleEnterRoom(room.roomid, room.invitation_status, room.role)
            }
            isProfessor={true}
            pendingAdjudicationsCount={room.pending_adjudications_count || 0}
            advisorsCount={room.advisors_count || 0}
            playersCount={room.players_count || 0}
            isGameReadyToStart={room.is_ready_to_start}
            scenarioTitle={room.scenario_title!}
            isLocked={room.is_locked}
            isOwner={user?.email === room.owner}
            isSimulating={room.is_simulating}
            onLeaveClick={() =>
              handleLeaveClick(room.roomid, user?.email === room.owner)
            }
          />
        ))}
      </RoomGroup>

      {exportModalOpen && roomToExport && (
        <ExportOptionsModal
          isOpen={exportModalOpen}
          onClose={() => {
            setExportModalOpen(false)
            setRoomToExport(null)
          }}
          roomId={roomToExport.id}
          isLive={roomToExport.isLive}
          gameMode={roomToExport.gameMode}
        />
      )}
      <ImportModal
        isOpen={importGameModalOpen}
        onClose={() => setImportGameModalOpen(false)}
        scope='full_game'
        onImportComplete={async ({ room_id } = {}) => {
          refreshDashboardData()
          if (!room_id) return
          try {
            await dispatch(getRoomDetails(room_id)).unwrap()
          } catch (e) {
            const message =
              typeof e === 'string'
                ? e
                : e instanceof Error
                  ? e.message
                  : 'Could not load room details'
            toast.warning(`${message}. Opening room details…`)
          }
          navigate(ROUTES.PROFESSOR.ROOM_DETAILS(room_id))
        }}
      />
    </div>
  )
}

export default Room
